//////////////////
// IMPORTS
//////////////////
// libraries
// state
import { setMenuDrawerOpen } from "#webapp/src/state/actions/chrome-extension.actions";
import { setPricingDialogOpen } from "#webapp/src/state/actions/payments.actions";
// material-ui
import {
  PlanStatusCallout,
  PlanStatusCalloutType,
} from "#webapp/src/components/plan-status-callout";

// other components
// util

import { AutopilotStatus } from "#webapp/src/components/autopilot-status";
import { TrialBannerSideMenu } from "#webapp/src/components/trial-banner-sidemenu";
import {
  IPaymentStatusBadge,
  PaymentStatusBadgeTextEnum,
} from "@clockwise/web-commons/src/util/feature-grid.util";
import { IProps as INavProps } from "../ChromeNavTypes";
import { ChromeNavTabs } from "./ChromeNavTabs";

import { useMonetization } from "#webapp/src/hooks/useMonetization";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { Nav } from "@clockwise/web-commons/src/components/nav";
import React, { useCallback } from "react";
import { connect } from "react-redux";
import { Environment } from "relay-runtime";
import { SchedulingLinksSidebar } from "../../scheduling-links-sidebar";
import { ChatSidebarBanner } from "./ChatSidebarBanner";
import { PlanBadge } from "./PlanBadge";

//////////////////
// TYPES
//////////////////
interface IContainer {
  showMenu?: boolean;
  showTabs?: boolean;
  showCollapse?: boolean;
  showLogo?: boolean;
  environment: Environment | null;
  org?: INavProps["org"];
}

interface IProps extends IContainer {
  isWebExtension: boolean;
  paymentStatusBadge: IPaymentStatusBadge | null;
  setMenuDrawerOpen: typeof setMenuDrawerOpen;
  setPricingDialogOpen: typeof setPricingDialogOpen;
}

interface ChromeNavHomePlanBadgeProps extends IContainer {
  paymentStatusBadge: IPaymentStatusBadge | null;
  setPricingDialogOpen: typeof setPricingDialogOpen;
}

//////////////////
// COMPONENT
//////////////////

const ChromeNavHomePlanBadge = ({
  paymentStatusBadge,
  setPricingDialogOpen,
}: ChromeNavHomePlanBadgeProps) => {
  if (!paymentStatusBadge) {
    return null;
  }

  const onBadgeClick = () => {
    track(TrackingEvents.PAYWALLS.CHROME_EXTENSION_PLAN_BADGE_CLICKED);
    setPricingDialogOpen(true);
  };

  let badgeText: string = paymentStatusBadge.text;
  if (paymentStatusBadge.text === PaymentStatusBadgeTextEnum.Pro) {
    badgeText = "Teams";
  }

  return <PlanBadge text={badgeText} type={paymentStatusBadge.type} onClick={onBadgeClick} />;
};

const ChromeNavHomeCmpt = ({
  setMenuDrawerOpen,
  org,
  environment,
  paymentStatusBadge,
  setPricingDialogOpen,
  showCollapse,
  showMenu,
  showTabs,
  showLogo,
}: IProps) => {
  const {
    shouldUserSeeJoinExistingPlan,
    showPlanStatusCalloutChrome,
    userIsOnActiveTrial,
    daysLeftInTrial,
  } = useMonetization();

  // ~-~-~-~-~-~-~-
  // Event Handlers
  // ~-~-~-~-~-~-~-
  const onClickMenu = useCallback(() => {
    setMenuDrawerOpen(true);
  }, [setMenuDrawerOpen]);

  const onClickClose = useCallback(() => {
    PostMessageManager.closeSidebar(true);
  }, []);

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  const onUpgradeTracking = useCallback(() => {
    const shouldShowJoinExistingPlans = shouldUserSeeJoinExistingPlan;
    if (shouldShowJoinExistingPlans) {
      return TrackingEvents.TRIAL.SIDEBAR_VIEW_TRIAL_EXPIRED_JOIN_PLAN_CLICKED;
    } else {
      return TrackingEvents.TRIAL.SIDEBAR_VIEW_TRIAL_EXPIRED_UPGRADE_CLICKED;
    }
  }, [shouldUserSeeJoinExistingPlan]);

  const maybeRenderAutopilotStatusRenderer = useCallback(() => {
    const orgTimeZone = (org && org.orgMeetingHours?.workingHours.timeZone) || undefined;

    if (!org || !orgTimeZone) {
      return "";
    }

    const calloutStyles = "cw-mt-[-5px] cw-mr-2.5 cw-ml-2.5";

    if (showPlanStatusCalloutChrome) {
      return (
        <div className={calloutStyles}>
          <PlanStatusCallout
            calloutType={PlanStatusCalloutType.TrialExpired_Sidebar}
            showJoinExistingPlan={shouldUserSeeJoinExistingPlan}
            trackingEvent={onUpgradeTracking()}
          />
        </div>
      );
    }

    return (
      <div style={{ margin: "-16px 10px 0" }}>
        <AutopilotStatus orgId={org.id} timeZone={orgTimeZone} />
      </div>
    );
  }, [org, showPlanStatusCalloutChrome, shouldUserSeeJoinExistingPlan, onUpgradeTracking]);

  return (
    <div cw-id="chrome-nav-home">
      {userIsOnActiveTrial && (
        <TrialBannerSideMenu
          daysLeft={daysLeftInTrial}
          showJoinExistingPlan={shouldUserSeeJoinExistingPlan}
        />
      )}
      {showLogo && (
        <Nav
          showMenu={showMenu}
          showCollapse={showCollapse}
          badge={
            <ChromeNavHomePlanBadge
              setPricingDialogOpen={setPricingDialogOpen}
              paymentStatusBadge={paymentStatusBadge}
              environment={environment}
            />
          }
          onClickMenu={onClickMenu}
          onClickCollapse={onClickClose}
        />
      )}
      {showTabs && (
        <>
          <ChatSidebarBanner />
          {maybeRenderAutopilotStatusRenderer()}
          {org && org?.id && (
            <div style={{ margin: "11px 11px" }} cw-id="scheduling-link-card">
              <SchedulingLinksSidebar />
            </div>
          )}
          <ChromeNavTabs />
        </>
      )}
    </div>
  );
};

//////////////////
// REDUX
//////////////////

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    paymentStatusBadge: state.featureGrid.planTierFeatures.paymentStatusBadge,
    isWebExtension: state.webExtension.isWebExtension,
  };
}

export const ChromeNavHome = connect(mapStateToProps, {
  setMenuDrawerOpen,
  setPricingDialogOpen,
})(ChromeNavHomeCmpt);
